import { type AccountRef, type UserStatus } from '@aysar/contracts';
import { useMemo } from 'react';

import { type BadgeTone } from '@/shared/components/ui/Badge/Badge.tokens';
import { statusTone } from '@/shared/utils/status-tone/status-tone.util';

import { type AccountStatusEmphasis } from './AccountRefCell.types';

export interface AccountRefCellView {
  /** The lookup came back short — the row still exists and must still be readable. */
  isMissing: boolean;
  email: string;
  isStaff: boolean;
  status: UserStatus | null;
  tone: BadgeTone;
}

/** The two states that change what a queue row is worth; everything else is context. */
const OUT_OF_SERVICE: ReadonlySet<UserStatus> = new Set<UserStatus>(['suspended', 'banned']);

export function useAccountRefCellLogic(
  account: AccountRef | null,
  emphasis: AccountStatusEmphasis,
): AccountRefCellView {
  return useMemo(() => {
    if (account === null) {
      return { isMissing: true, email: '', isStaff: false, status: null, tone: 'neutral' };
    }

    const tone =
      emphasis === 'lifecycle' || OUT_OF_SERVICE.has(account.status)
        ? statusTone(account.status)
        : 'neutral';

    return {
      isMissing: false,
      email: account.email,
      isStaff: account.isStaff,
      status: account.status,
      tone,
    };
  }, [account, emphasis]);
}
